// ---------------------------------------------------------------------------
// THE UNIVERSE BRAND, as DESIGN.md §2–§4 names it. The only file in the client
// that spells these values: everything else reads a token from here.
//
// The brand sheet gives four colours and a gradient. Every derived step is
// one of the two stops, their exact midpoint, or the navy taken darker —
// nothing is invented (the contrast ratios are quoted in DESIGN.md §4).
// ---------------------------------------------------------------------------

import { alpha } from '@mui/material/styles';

export const UNIVERSE = Object.freeze({
  navy: '#01266D',
  light: '#80B4ED',
  white: '#FFFFFF',
  ink: '#333333',
  // the exact midpoint of the gradient
  mid: '#416DAD',
  // the navy, pressed
  deep: '#011C52',
  // the rail's ground: the navy taken down to 6% lightness — as deep as the
  // rail was before it took the brand hue (the owner, 07/10/2026: "un po'
  // più scuro com'era prima")
  night: '#040B1B',
  // the sheet's 60° inclination is Illustrator's; CSS measures from north
  gradient: 'linear-gradient(30deg, #01266D 0%, #80B4ED 100%)'
});

// THE LOCK-UP (DESIGN.md §2 "Logo"): fixed artwork from the brand pack. The
// White version on navy and on the gradient, the Colour version on paper; the
// globe alone where the wordmark would be illegible (the rail header, the
// favicon). Never recoloured, stretched or placed on glass.
export const BRAND_LOGO_WHITE = '/logo/universe-white.svg';
export const BRAND_LOGO_COLOR = '/logo/universe-color.svg';
export const BRAND_ICON_WHITE = '/logo/universe-icon-white.svg';
export const BRAND_ICON_COLOR = '/logo/universe-icon-color.svg';

// THE UI FACE: Inter Tight, the verdict of the font validation gate of
// 07/10/2026 (DESIGN.md §3). On the real list at 1440 Montserrat's wide
// letterforms truncated "Formatting in Progress" and "Emanuela Ballo" where
// both candidates set them whole; Inter Tight read crisper at 13px than Geist,
// its tabular figures line up, and it carries no other product's identity.
// Montserrat stays the brand face beside Legian (sign-in, chooser, banners).
// One constant to flip.
export const UNIVERSE_FONT =
  '"Inter Tight", "Montserrat", "Public Sans", system-ui, sans-serif';

// The dark-mode accent steps (DESIGN.md §4, measured on #111B33).
const DARK_ACCENT = Object.freeze({
  main: UNIVERSE.light,
  hover: '#A3C7F2',
  pressed: '#5E9BE3'
});

const isLight = (theme) => theme.palette.mode === 'light';

/** The accent: navy on paper, light blue on the night. */
export const accentOf = (theme) =>
  isLight(theme) ? UNIVERSE.navy : DARK_ACCENT.main;
export const accentHoverOf = (theme) =>
  isLight(theme) ? UNIVERSE.deep : DARK_ACCENT.hover;
export const accentPressedOf = (theme) =>
  isLight(theme) ? UNIVERSE.deep : DARK_ACCENT.pressed;
/** Text on the accent. */
export const onAccentOf = (theme) =>
  isLight(theme) ? UNIVERSE.white : UNIVERSE.navy;

/**
 * The FILL of a primary button and of the motion button's circle: the navy
 * on paper; on the night the gradient's midpoint, `universe.mid`, so the
 * button is a solid blue with white on it rather than a pale pill with navy
 * on it (the owner, 07/10/2026: the light-blue fill "doesn't look good in
 * dark mode"). The accent proper — text, links, marks, the underline — stays
 * the light blue on the night. White on `mid` measures 5.2:1.
 */
export const fillOf = (theme) =>
  isLight(theme) ? UNIVERSE.navy : UNIVERSE.mid;
export const fillHoverOf = (theme) =>
  isLight(theme) ? UNIVERSE.deep : '#4E7BBE';
export const fillPressedOf = (theme) =>
  isLight(theme) ? UNIVERSE.deep : '#355E99';
/** Text on the fill: white in both modes. */
export const onFillOf = () => UNIVERSE.white;

/** The accent as a tint: hover .06 / .10, selected .08 / .14, soft fill .12 / .18. */
export const accentTint = (theme, step = 'hover') => {
  const light = isLight(theme);
  const by = {
    hover: light ? 0.06 : 0.1,
    selected: light ? 0.08 : 0.14,
    soft: light ? 0.12 : 0.18
  };
  return alpha(accentOf(theme), by[step] ?? by.hover);
};

/** The focus ring: a 1px accent border (the 3:1 boundary) and a 3px halo. */
export const focusRingSx = (theme) => ({
  outline: 'none',
  boxShadow: `0 0 0 1px ${accentOf(theme)}, 0 0 0 4px ${alpha(
    accentOf(theme),
    isLight(theme) ? 0.24 : 0.32
  )}`
});

/** The hairline and the boundary of a control (DESIGN.md §4, ≥ 3:1). */
export const hairlineOf = (theme) =>
  isLight(theme) ? '#E1E7F0' : 'rgba(255,255,255,0.08)';
export const controlLineOf = (theme) =>
  isLight(theme) ? '#7D8699' : 'rgba(255,255,255,0.40)';
/**
 * The softer line of a filter field and of a secondary button (DESIGN.md
 * §18): a grey the eye rests on (the owner, 07/10/2026: "a bit greyer, more
 * elegant"); the control line above stays for marks that must reach 3:1.
 */
export const fieldLineOf = (theme) =>
  isLight(theme) ? '#D5DCE6' : 'rgba(255,255,255,0.14)';
/** The ink of what a field holds: a step greyer than body text. */
export const fieldInkOf = (theme) => (isLight(theme) ? '#4B5563' : '#C8D0DA');
/**
 * The ink of a list row's data — the client's name, the dates, the figures —
 * on paper: the brand navy (14.01:1 on white), the reference's own ink, so a
 * row reads in the brand's hue rather than in black (the owner, 21/10/2026,
 * on the translation projects list: "non mi piace che sia testo nero"). The
 * navy taken darker was tried first and still read as black. On the night,
 * the body text as before.
 */
export const rowInkOf = (theme) => (isLight(theme) ? UNIVERSE.navy : theme.palette.text.primary);

/** The sunken well a secondary head or an inset region sits on. */
export const sunkenOf = (theme) => (isLight(theme) ? '#EDF1F7' : '#0D172E');

/**
 * The canvas of a WORKSPACE page: a step deeper than the portal's canvas, so
 * the region sheets stand off it (the owner, 08/10/2026: "still melted with
 * the bg"). Painted by the page while it is mounted, never by the theme —
 * the lists keep the portal canvas until the same verdict reaches them.
 */
export const workspaceCanvasOf = (theme) =>
  isLight(theme) ? '#EEF2F7' : '#070E1F';

/** Geometry: radius in px (DESIGN.md §6) and the touch hit area (§24). */
// A button is a PILL (the owner, 08/10/2026: "all buttons should have the
// Claim project rounded shape, also in the lists"): `button` is the full
// radius, on every button and icon button; a control keeps its 6.
export const RADIUS = Object.freeze({
  control: 6,
  button: 999,
  small: 8,
  panel: 10,
  overlay: 12,
  modal: 14
});
export const HIT = Object.freeze({ touch: 44, pointer: 32 });

/**
 * THE INSPECTOR'S WIDTHS (DESIGN.md §5 "Inspector", §14): the pane a selected
 * row's context opens in is never one product-wide pixel width — it is a
 * share of the workspace where a split is viable, a share of the window as an
 * overlay, and a sheet on a phone.
 *
 * `overlay` was a flat 440 and read as a slot rather than a workspace (the
 * owner, 08/10/2026: "it's very narrow"). It is now a share of the window
 * between a floor and a ceiling: at 1280 it is 512, at 1660 it is 664, and it
 * never takes more than half the screen. Facts come out three to a row and a
 * note is written at a width somebody would actually write at.
 */
export const PANE = Object.freeze({
  split: '35%',
  overlay: 'clamp(420px, 40vw, 720px)',
  min: 360
});

/** Durations in ms (DESIGN.md §11). */
export const MOTION = Object.freeze({
  fast: 120,
  standard: 200,
  spatial: 280,
  exit: 160
});

/** Lining, tabular figures for every column of numbers. */
export const tabularSx = {
  fontVariantNumeric: 'tabular-nums lining-nums',
  fontFeatureSettings: '"tnum" 1, "lnum" 1'
};

/** An invisible 44px hit area around a control that stays its visual size. */
export const touchHitSx = {
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: '50%',
    width: HIT.touch,
    height: HIT.touch,
    transform: 'translate(-50%, -50%)'
  }
};
